
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, Search, Database, Wifi } from 'lucide-react';
import { ArxivSearchParams } from './types';
import { ARXIV_CATEGORY_GROUPS } from './constants';
import { exportToCSV } from './utils';
import { isWithinCacheWindow } from '@/lib/api/arxiv-cache';

interface ArxivSearchFormProps {
  searchParams: ArxivSearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<ArxivSearchParams>>;
  onSearch: () => void;
  loading: boolean;
  records: any[];
  isFromCache?: boolean;
  cacheWindow?: { from: string; until: string };
}

const ArxivSearchForm: React.FC<ArxivSearchFormProps> = ({
  searchParams,
  setSearchParams,
  onSearch,
  loading,
  records,
  isFromCache = false,
  cacheWindow
}) => {
  const willUseCache = cacheWindow && isWithinCacheWindow(searchParams.from, searchParams.until);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          arXiv Bulk Metadata Fetch
        </CardTitle>
        <CardDescription>
          Fetch paper metadata from arXiv using the OAI-PMH interface. 
          You can filter by category and date range. Select specific subcategories for more targeted results.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Cache Status Banner */}
        {cacheWindow && (
          <div className="bg-secondary/50 rounded-lg p-3 border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Cache Available: {cacheWindow.from} to {cacheWindow.until}</span>
              </div>
              <Badge variant={willUseCache ? "secondary" : "outline"} className="flex items-center gap-1">
                {willUseCache ? (
                  <>
                    <Database className="h-3 w-3" />
                    Will use Cache
                  </>
                ) : (
                  <>
                    <Wifi className="h-3 w-3" />
                    Will fetch Live
                  </>
                )}
              </Badge>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="set">Category/Set</Label>
            <Select 
              value={searchParams.set} 
              onValueChange={(value) => setSearchParams(prev => ({ ...prev, set: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="max-h-96">
                {ARXIV_CATEGORY_GROUPS.map((group) => (
                  <div key={group.name}>
                    <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground border-b">
                      {group.name}
                    </div>
                    {group.categories.map((category) => (
                      <SelectItem key={category.value} value={category.value} className="pl-4">
                        <div className="flex flex-col">
                          <span className="font-medium">{category.label}</span>
                          <span className="text-xs text-muted-foreground">{category.value}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Select a specific arXiv category. Use subcategories (e.g., cs.AI, cs.LG) for more targeted results.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="from">From Date</Label>
            <Input
              id="from"
              type="date"
              value={searchParams.from}
              onChange={(e) => setSearchParams(prev => ({ ...prev, from: e.target.value }))}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="until">Until Date</Label>
            <Input
              id="until"
              type="date"
              value={searchParams.until}
              onChange={(e) => setSearchParams(prev => ({ ...prev, until: e.target.value }))}
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            onClick={onSearch} 
            disabled={loading}
            className="flex items-center gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search arXiv
          </Button>
          
          {records.length > 0 && (
            <Button 
              variant="outline" 
              onClick={() => exportToCSV(records)}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Results Status */}
        {records.length > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Found {records.length} records</span>
            <Badge variant={isFromCache ? "secondary" : "outline"} className="flex items-center gap-1">
              {isFromCache ? (
                <>
                  <Database className="h-3 w-3" />
                  From Cache
                </>
              ) : (
                <>
                  <Wifi className="h-3 w-3" />
                  Live from arXiv
                </>
              )}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ArxivSearchForm;
